import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PromoBanner from "../Components/PromoBanner";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { pushDataLayerEvent } from '../utils/tracking';

const JOB_TYPES = [
  "Stickers",
  "Business Cards",
  "Flyers",
  "Invitations",
  "Greeting Cards",
  "Postcards",
  "Certificates",
  "Gift Vouchers",
  "Paper Tags",
  "Brochures",
  "Packaging",
];

const LAMINATION_TYPES = ["Matt", "Glossy", "Soft Touch"];

const SIDE_OPTIONS = [
  { value: "no", label: "No" },
  { value: "one-side", label: "Yes, one side" },
  { value: "two-sides", label: "Yes, two sides" },
];

const YES_NO_OPTIONS = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

const INITIAL_FORM_DATA = {
  name: "",
  email: "",
  contactNumber: "",
  jobType: "",
  specialColours: "no",
  spotUv: "no",
  foilColours: "no",
  foilSide1: "",
  foilSide2: "",
  lamination: "no",
  laminationSide1: "",
  laminationSide2: "",
  customShapes: "no",
  personalisation: "no",
  variablesCount: "",
  designService: "no",
  furtherDetails: "",
};

const rowClassName = "grid gap-2 lg:grid-cols-[180px_minmax(0,1fr)] lg:items-center";
const labelClassName = "text-[13px] font-semibold text-[#4d4d4d]";
const inputClassName =
  "h-[38px] w-full rounded-[2px] border border-[#e6e6e6] bg-white px-3 text-[13px] text-[#656565] outline-none transition placeholder:text-[#c9c9c9] focus:border-[#ec73ad]";
const textareaClassName =
  "w-full rounded-[2px] border border-[#e6e6e6] bg-white px-3 py-3 text-[13px] text-[#656565] outline-none transition placeholder:text-[#c9c9c9] focus:border-[#ec73ad]";

const supportsAtLeastOneSide = (value) => value !== "no";
const supportsTwoSides = (value) => value === "two-sides";

const ErrorText = ({ message }) =>
  message ? <p className="mt-1 text-[12px] text-red-500">{message}</p> : null;

const SelectField = ({ name, value, onChange, options, placeholder, disabled = false }) => (
  <div className="relative">
    <select
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`${inputClassName} appearance-none pr-10 disabled:cursor-not-allowed disabled:bg-[#fafafa] disabled:text-[#b8b8b8]`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[#c7c7c7]">
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  </div>
);

const ChoiceOptions = ({ name, value, options, onChange }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
    {options.map((option) => (
      <label key={option.value} className="inline-flex items-center gap-2 text-[13px] text-[#565656]">
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={onChange}
          className="peer sr-only"
        />
        <span className="h-[13px] w-[13px] border border-[#888] bg-white peer-checked:border-[#ec3f8e] peer-checked:bg-[#ec3f8e]" />
        <span>{option.label}</span>
      </label>
    ))}
  </div>
);

const toApiSideValue = (value) => {
  switch (value) {
    case "one-side":
      return "one_side";
    case "two-sides":
      return "two_sides";
    default:
      return "no";
  }
};

const toBoolean = (value) => value === "yes";

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const BespokeRequest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupStatus, setPopupStatus] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate("/");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "foilColours") {
        if (!supportsAtLeastOneSide(value)) {
          next.foilSide1 = "";
          next.foilSide2 = "";
        } else if (!supportsTwoSides(value)) {
          next.foilSide2 = "";
        }
      }

      if (name === "lamination") {
        if (!supportsAtLeastOneSide(value)) {
          next.laminationSide1 = "";
          next.laminationSide2 = "";
        } else if (!supportsTwoSides(value)) {
          next.laminationSide2 = "";
        }
      }

      if (name === "personalisation" && value !== "yes") {
        next.variablesCount = "";
      }

      return next;
    });

    setFormErrors((prev) => {
      const next = { ...prev, [name]: "" };

      if (name === "foilColours") {
        next.foilSide1 = "";
        next.foilSide2 = "";
      }

      if (name === "lamination") {
        next.laminationSide1 = "";
        next.laminationSide2 = "";
      }

      if (name === "personalisation") {
        next.variablesCount = "";
      }

      return next;
    });
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.contactNumber.trim()) errors.contactNumber = "Contact number is required";
    if (!formData.jobType) errors.jobType = "Please select a job type";

    if (formData.personalisation === "yes" && !formData.variablesCount.trim()) {
      errors.variablesCount = "Please tell us how many variables you need";
    }

    if (supportsAtLeastOneSide(formData.lamination) && !formData.laminationSide1) {
      errors.laminationSide1 = "Select side 1 lamination";
    }

    if (supportsTwoSides(formData.lamination) && !formData.laminationSide2) {
      errors.laminationSide2 = "Select side 2 lamination";
    }

    if (supportsAtLeastOneSide(formData.foilColours) && !formData.foilSide1.trim()) {
      errors.foilSide1 = "Enter side 1 foil colours count";
    }

    if (supportsTwoSides(formData.foilColours) && !formData.foilSide2.trim()) {
      errors.foilSide2 = "Enter side 2 foil colours count";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://backend.printtrio.co.uk/api/request/bespoke-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          contact_number: formData.contactNumber.trim(),
          job_type: formData.jobType,
          special_colours: toApiSideValue(formData.specialColours),
          spot_uv: toApiSideValue(formData.spotUv),
          foil_colours: toApiSideValue(formData.foilColours),
          foil_colours_side_1: supportsAtLeastOneSide(formData.foilColours) ? toNumberOrNull(formData.foilSide1) : null,
          foil_colours_side_2: supportsTwoSides(formData.foilColours) ? toNumberOrNull(formData.foilSide2) : null,
          lamination: toApiSideValue(formData.lamination),
          lamination_type_side_1: supportsAtLeastOneSide(formData.lamination) ? formData.laminationSide1 : null,
          lamination_type_side_2: supportsTwoSides(formData.lamination) ? formData.laminationSide2 : null,
          custom_shapes: toBoolean(formData.customShapes),
          personalisation: toBoolean(formData.personalisation),
          variables_count: toBoolean(formData.personalisation) ? toNumberOrNull(formData.variablesCount) : null,
          design_service: toBoolean(formData.designService),
          further_details: formData.furtherDetails.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        pushDataLayerEvent({
          event: 'generate_lead',
          form_name: 'bespoke_request_form',
          form_location: '/bespoke-request',
          value: 0,
          currency: 'GBP',
        });
        setPopupStatus("success");
        setPopupMessage(data.data || "Print job request submitted successfully");
        setShowPopup(true);
        setFormData(INITIAL_FORM_DATA);
        setFormErrors({});
      } else {
        const errorMessage =
          typeof data?.errors === "string"
            ? data.errors
            : Array.isArray(data?.errors)
              ? data.errors.join(", ")
              : data?.data || "Something went wrong. Please try again.";

        setPopupStatus("error");
        setPopupMessage(errorMessage);
        setShowPopup(true);
      }
    } catch (error) {
      setPopupStatus("error");
      setPopupMessage("An error occurred. Please try again.");
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PromoBanner />
      <Navbar />
      <section className="bg-white px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1040px] border border-[#f2efef] bg-white px-5 py-8 shadow-[0_4px_18px_rgba(0,0,0,0.04)] sm:px-8 lg:px-10">
          <div className="mx-auto max-w-[760px] text-center">
            <h1 className="text-[24px] font-semibold text-[#e14f92] sm:text-[28px]">Bespoke requests</h1>
            <p className="mt-3 text-[12px] leading-[1.9] text-[#666] sm:text-[13px]">
              If you have any specific requests involving complex or high end finishes such as custom colors,
              foils, spot uv, bespoke shapes, or personalized names and design details, we&apos;re more than
              happy to accommodate them. Simply fill in the form below with your requirements so we can assist
              you in the most effective way possible.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-[860px] space-y-4">
            <div className={rowClassName}>
              <label htmlFor="bespoke-name" className={labelClassName}>
                Your name*
              </label>
              <div>
                <input
                  id="bespoke-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className={inputClassName}
                />
                <ErrorText message={formErrors.name} />
              </div>
            </div>

            <div className={rowClassName}>
              <label htmlFor="bespoke-email" className={labelClassName}>
                Your email*
              </label>
              <div>
                <input
                  id="bespoke-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your email"
                  className={inputClassName}
                />
                <ErrorText message={formErrors.email} />
              </div>
            </div>

            <div className={rowClassName}>
              <label htmlFor="bespoke-contact" className={labelClassName}>
                Your contact number*
              </label>
              <div>
                <input
                  id="bespoke-contact"
                  type="text"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="Your contact number"
                  className={inputClassName}
                />
                <ErrorText message={formErrors.contactNumber} />
              </div>
            </div>

            <div className={rowClassName}>
              <label htmlFor="bespoke-job-type" className={labelClassName}>
                What type of job do you require
              </label>
              <div>
                <SelectField
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  options={JOB_TYPES}
                  placeholder="Select job type"
                />
                <ErrorText message={formErrors.jobType} />
              </div>
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require special colours</span>
              <ChoiceOptions
                name="specialColours"
                value={formData.specialColours}
                options={SIDE_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require spot uv</span>
              <ChoiceOptions name="spotUv" value={formData.spotUv} options={SIDE_OPTIONS} onChange={handleChange} />
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require foil colours</span>
              <ChoiceOptions
                name="foilColours"
                value={formData.foilColours}
                options={SIDE_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>How many foil colours</span>
              <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,140px)_auto_minmax(0,140px)] sm:items-start">
                <span className="pt-2 text-[13px] text-[#666]">Side 1</span>
                <div>
                  <input
                    type="number"
                    min="0"
                    name="foilSide1"
                    value={formData.foilSide1}
                    onChange={handleChange}
                    disabled={!supportsAtLeastOneSide(formData.foilColours)}
                    className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-[#fafafa] disabled:text-[#b8b8b8]`}
                  />
                  <ErrorText message={formErrors.foilSide1} />
                </div>
                <span className="pt-2 text-[13px] text-[#666]">Side 2</span>
                <div>
                  <input
                    type="number"
                    min="0"
                    name="foilSide2"
                    value={formData.foilSide2}
                    onChange={handleChange}
                    disabled={!supportsTwoSides(formData.foilColours)}
                    className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-[#fafafa] disabled:text-[#b8b8b8]`}
                  />
                  <ErrorText message={formErrors.foilSide2} />
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require lamination</span>
              <ChoiceOptions
                name="lamination"
                value={formData.lamination}
                options={SIDE_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>What type of lamination</span>
              <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,180px)_auto_minmax(0,180px)] sm:items-start">
                <span className="pt-2 text-[13px] text-[#666]">Side 1</span>
                <div>
                  <SelectField
                    name="laminationSide1"
                    value={formData.laminationSide1}
                    onChange={handleChange}
                    options={LAMINATION_TYPES}
                    placeholder="Lamination type"
                    disabled={!supportsAtLeastOneSide(formData.lamination)}
                  />
                  <ErrorText message={formErrors.laminationSide1} />
                </div>
                <span className="pt-2 text-[13px] text-[#666]">Side 2</span>
                <div>
                  <SelectField
                    name="laminationSide2"
                    value={formData.laminationSide2}
                    onChange={handleChange}
                    options={LAMINATION_TYPES}
                    placeholder="Lamination type"
                    disabled={!supportsTwoSides(formData.lamination)}
                  />
                  <ErrorText message={formErrors.laminationSide2} />
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require custom-shapes</span>
              <ChoiceOptions
                name="customShapes"
                value={formData.customShapes}
                options={YES_NO_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require personalisation</span>
              <ChoiceOptions
                name="personalisation"
                value={formData.personalisation}
                options={YES_NO_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <label htmlFor="bespoke-variables" className={labelClassName}>
                How many variables
              </label>
              <div className="max-w-[280px]">
                <input
                  id="bespoke-variables"
                  type="number"
                  min="0"
                  name="variablesCount"
                  value={formData.variablesCount}
                  onChange={handleChange}
                  disabled={formData.personalisation !== "yes"}
                  className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-[#fafafa] disabled:text-[#b8b8b8]`}
                />
                <ErrorText message={formErrors.variablesCount} />
              </div>
            </div>

            <div className={rowClassName}>
              <span className={labelClassName}>Do you require design service</span>
              <ChoiceOptions
                name="designService"
                value={formData.designService}
                options={YES_NO_OPTIONS}
                onChange={handleChange}
              />
            </div>

            <div className={rowClassName}>
              <label htmlFor="bespoke-details" className={labelClassName}>
                Further details
              </label>
              <textarea
                id="bespoke-details"
                name="furtherDetails"
                value={formData.furtherDetails}
                onChange={handleChange}
                placeholder="Any further details for your request"
                rows="5"
                className={textareaClassName}
              />
            </div>

            <div className="flex justify-center pt-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[84px] rounded-[3px] bg-[#ec3f8e] px-6 py-2 text-[12px] font-semibold text-white transition hover:bg-[#d92a7c] disabled:cursor-not-allowed disabled:bg-[#f2a1c8]"
              >
                {isSubmitting ? "Sending..." : "Send"}
              </button>
            </div>
          </form>
        </div>
      </section>
      <Footer />

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-[20px] bg-white p-6 text-center shadow-xl">
            <h2 className="text-2xl font-bold text-[#30243b]">
              {popupStatus === "success" ? "Success!" : "Error"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-[#5f5167]">{popupMessage}</p>
            <button
              onClick={handleClosePopup}
              className="mt-6 inline-flex rounded-full bg-[#EC008C] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#d70080]"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BespokeRequest;
